import { CollabStatus } from '../types/collab';
import { CampaignType } from '../types/campaign';

/**
 * Which collab actions a CREATOR may take. Shared rather than duplicated because these gate a
 * server-side endpoint as well as the buttons: if the two copies drifted, the UI would offer an
 * action the API rejects, or worse, the API would allow one the UI never intended.
 */

/** Why a creator may not cancel, or null when they may. Ordered most-fundamental first. */
export type InfluencerCancelBlockedReason =
  | 'unsupported-campaign-type'
  | 'wrong-status'
  | 'within-cutoff';

export interface InfluencerCancelContext {
  status?: CollabStatus | string;
  campaignType?: CampaignType | string;
  /** The scheduled visit instant, or null when none is set. */
  scheduledCollabDate?: Date | null;
  /** Injectable for tests; defaults to now. */
  now?: Date;
}

/**
 * Only visit-based campaigns have a cancellation flow. Shopify and ship-to-home creators are
 * already holding product by the time they could cancel, so the app never offers it — and the
 * endpoint must not either, because cancelling releases the brand's reserved funds.
 */
export function isCancellableCampaignTypeForInfluencer(campaignType?: CampaignType | string): boolean {
  return campaignType === 'inStore' || campaignType === 'retail';
}

/** A creator may cancel only an accepted visit that has not happened yet. */
export function isInfluencerCancellableStatus(status?: CollabStatus | string): boolean {
  return status === 'pendingRedemption';
}

/**
 * How much notice a creator must give to cancel on their own.
 *
 * Named and exported rather than inlined because the number appears in the refusal copy too, and
 * the two drifting would tell creators a cutoff the code does not enforce. Every caller — the
 * Cancel link, the /collabs/cancel endpoint and the inbox cancel agent — reads it from here.
 */
export const INFLUENCER_CANCEL_CUTOFF_HOURS = 3;

/**
 * How much notice a creator must give to move a visit on their own.
 *
 * Was 24 hours, inlined in the frontend. Lowered to match the cancel cutoff because the inbox
 * agent now sends creators to this UI rather than negotiating a new time in the thread — and the
 * agent has never had a proximity cutoff, so keeping 24 here would have offered a Reschedule
 * button that led to a refusal page in the 24h-to-3h window, which is exactly the window where
 * moving a visit is the only thing left that helps.
 *
 * Deliberately a separate constant from the cancel one despite holding the same value: they answer
 * different questions, and a future decision to make cancelling stricter than rescheduling (or the
 * reverse) should not have to untangle one number serving two rules.
 */
export const INFLUENCER_RESCHEDULE_CUTOFF_HOURS = 3;

/**
 * How many times a creator may move a visit themselves.
 *
 * Shared rather than frontend-only because the inbox agent now has to answer the same question
 * before it offers a Reschedule chip: that chip leads to the app's scheduling screen, which
 * consumes an attempt, so offering it to someone with none left is a button that leads to a
 * refusal. Two copies of this rule would put the agent and the screen out of step.
 */
export const MAX_INFLUENCER_RESCHEDULES = 1;

/**
 * How many self-serve reschedules the creator has already used.
 *
 * Counts ONLY 'influencer_reschedule' logs. A first booking is 'influencer_schedule' and a
 * brand-requested move is its own action — neither spends the creator's allowance, which is why
 * this counts the specific action rather than the length of the log.
 */
export function countInfluencerReschedules(
  scheduleLogs?: { action?: string }[] | null,
): number {
  if (!scheduleLogs) return 0;
  return scheduleLogs.filter((log) => log?.action === 'influencer_reschedule').length;
}

/** True when the creator still has a self-serve reschedule left on this collab. */
export function hasInfluencerReschedulesLeft(
  scheduleLogs?: { action?: string }[] | null,
): boolean {
  return countInfluencerReschedules(scheduleLogs) < MAX_INFLUENCER_RESCHEDULES;
}

/**
 * True when the visit is too close — or too late — for the creator to move it themselves.
 *
 * Unlike the cancel cutoff, a visit ALREADY IN THE PAST counts as blocked. Cancelling a passed
 * visit is harmless (the table is no longer held); rescheduling one is a different conversation
 * entirely, and the app routes it to the "did you already visit?" flow instead.
 */
export function isWithinInfluencerRescheduleCutoff(
  scheduledCollabDate?: Date | null,
  now: Date = new Date()
): boolean {
  if (!scheduledCollabDate) return false;
  const cutoff = new Date(now.getTime() + INFLUENCER_RESCHEDULE_CUTOFF_HOURS * 60 * 60 * 1000);
  return scheduledCollabDate <= cutoff;
}

/**
 * True when the visit is close enough that cancelling is closed off: upcoming, and less than
 * INFLUENCER_CANCEL_CUTOFF_HOURS away. A visit already in the past is NOT within the cutoff — the
 * venue has stopped holding the table, so the late-cancellation concern no longer applies.
 * The frontend's cancel gate delegates here, so the disabled link and the endpoint cannot drift.
 */
export function isWithinInfluencerCancelCutoff(
  scheduledCollabDate?: Date | null,
  now: Date = new Date()
): boolean {
  if (!scheduledCollabDate) return false;
  const cutoff = new Date(now.getTime() + INFLUENCER_CANCEL_CUTOFF_HOURS * 60 * 60 * 1000);
  return scheduledCollabDate <= cutoff && now < scheduledCollabDate;
}

/**
 * The single composed cancel rule, matching what the UI offers: a visit-based campaign, in an
 * accepted-but-unvisited state, more than INFLUENCER_CANCEL_CUTOFF_HOURS out. Returns the
 * specific reason so the API can explain the refusal rather than returning a bare 409.
 */
export function getInfluencerCancelBlockedReason(
  context: InfluencerCancelContext
): InfluencerCancelBlockedReason | null {
  if (!isCancellableCampaignTypeForInfluencer(context.campaignType)) return 'unsupported-campaign-type';
  if (!isInfluencerCancellableStatus(context.status)) return 'wrong-status';
  if (isWithinInfluencerCancelCutoff(context.scheduledCollabDate, context.now ?? new Date())) {
    return 'within-cutoff';
  }
  return null;
}

export function canInfluencerCancelCollab(context: InfluencerCancelContext): boolean {
  return getInfluencerCancelBlockedReason(context) === null;
}

/** Human-readable refusal, used for the API error and safe to surface to the creator. */
export function describeInfluencerCancelBlockedReason(reason: InfluencerCancelBlockedReason): string {
  switch (reason) {
    case 'unsupported-campaign-type':
      return 'This collab type cannot be canceled from the app';
    case 'within-cutoff':
      return `This collab can no longer be canceled within ${INFLUENCER_CANCEL_CUTOFF_HOURS} hours of the visit`;
    case 'wrong-status':
    default:
      return 'This collab can no longer be canceled';
  }
}

/**
 * A creator may decline only an invite they have not accepted. Declining anything later would
 * stamp `collabRejected` over live or finished state without releasing reserved funds — nothing
 * was ever funded on an un-accepted invite, so decline has no wallet step to run.
 */
export function canInfluencerDeclineCollab(status?: CollabStatus | string): boolean {
  return status === 'pendingInfluencerApproval';
}
